<script lang="ts">
  import { authState } from '../../lib/auth.svelte';
  import ResendWelcomeEmail from './ResendWelcomeEmail.svelte';

  interface Props {
    subscriptions: any[];
    loading: boolean;
    onNavigate: (nav: string) => void;
  }

  let { subscriptions, loading, onNavigate }: Props = $props();
</script>

<!-- Welcome Banner -->
<div class="welcome-banner">
  <div class="welcome-content">
    <div class="welcome-text">
      <h2>Account Management Portal</h2>
      <p>Manage your subscription, team members, business settings, and integrations from one place.</p>
    </div>
    <a href="https://app.equosnine.com" target="_blank" rel="noopener noreferrer" class="launch-app-btn">
      <span>Launch EQUOS App</span>
      <svg viewBox="0 0 20 20" fill="currentColor">
        <path fill-rule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clip-rule="evenodd" />
      </svg>
    </a>
  </div>
</div>

<!-- Launch App Card (Mobile-friendly prominent placement) -->
<div class="launch-app-card">
  <div class="launch-app-icon">
    <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect width="48" height="48" rx="10" fill="white"/>
      <rect x="1" y="1" width="46" height="46" rx="9" stroke="currentColor" stroke-width="2"/>
      <path d="M14 14h8v2h-5.17l10.59 10.59-1.42 1.41L16 17.83V23h-2v-9z" fill="currentColor"/>
      <path d="M34 34h-8v-2h5.17L20.58 21.41l1.42-1.41L32 30.17V25h2v9z" fill="currentColor"/>
    </svg>
  </div>
  <div class="launch-app-content">
    <h3>Ready to work?</h3>
    <p>Access the full EQUOS application to manage shipments, inventory, and more.</p>
  </div>
  <a href="https://app.equosnine.com" target="_blank" rel="noopener noreferrer" class="launch-app-action">
    Open App
    <svg viewBox="0 0 20 20" fill="currentColor">
      <path d="M11 3a1 1 0 100 2h2.586l-6.293 6.293a1 1 0 101.414 1.414L15 6.414V9a1 1 0 102 0V4a1 1 0 00-1-1h-5z" />
      <path d="M5 5a2 2 0 00-2 2v8a2 2 0 002 2h8a2 2 0 002-2v-3a1 1 0 10-2 0v3H5V7h3a1 1 0 000-2H5z" />
    </svg>
  </a>
</div>

<div class="overview-grid">
  <div class="overview-card">
    <div class="overview-icon subscription-icon">
      <svg viewBox="0 0 20 20" fill="currentColor">
        <path d="M4 4a2 2 0 00-2 2v1h16V6a2 2 0 00-2-2H4z" />
        <path fill-rule="evenodd" d="M18 9H2v5a2 2 0 002 2h12a2 2 0 002-2V9zM4 13a1 1 0 011-1h1a1 1 0 110 2H5a1 1 0 01-1-1zm5-1a1 1 0 100 2h1a1 1 0 100-2H9z" clip-rule="evenodd" />
      </svg>
    </div>
    <div class="overview-content">
      <h3>Subscription</h3>
      {#if loading}
        <p>Loading...</p>
      {:else if subscriptions.length > 0}
        <p class="status-active">{subscriptions[0]?.items?.[0]?.price?.product?.name || 'Active'}</p>
      {:else}
        <p class="status-none">No active plan</p>
      {/if}
    </div>
    <button class="overview-action" onclick={() => onNavigate('subscription')}>
      Manage
      <svg viewBox="0 0 20 20" fill="currentColor">
        <path fill-rule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clip-rule="evenodd" />
      </svg>
    </button>
  </div>

  <div class="overview-card">
    <div class="overview-icon business-icon">
      <svg viewBox="0 0 20 20" fill="currentColor">
        <path fill-rule="evenodd" d="M4 4a2 2 0 012-2h8a2 2 0 012 2v12a1 1 0 110 2h-3a1 1 0 01-1-1v-2a1 1 0 00-1-1H9a1 1 0 00-1 1v2a1 1 0 01-1 1H4a1 1 0 110-2V4zm3 1h2v2H7V5zm2 4H7v2h2V9zm2-4h2v2h-2V5zm2 4h-2v2h2V9z" clip-rule="evenodd" />
      </svg>
    </div>
    <div class="overview-content">
      <h3>Businesses</h3>
      <p>Manage your business units</p>
    </div>
    <button class="overview-action" onclick={() => onNavigate('businesses')}>
      View
      <svg viewBox="0 0 20 20" fill="currentColor">
        <path fill-rule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clip-rule="evenodd" />
      </svg>
    </button>
  </div>

  {#if authState.isAccountAdmin || authState.isSystemAdmin}
  <div class="overview-card">
    <div class="overview-icon team-icon">
      <svg viewBox="0 0 20 20" fill="currentColor">
        <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z" />
      </svg>
    </div>
    <div class="overview-content">
      <h3>Team</h3>
      <p>Manage team members</p>
    </div>
    <button class="overview-action" onclick={() => onNavigate('team')}>
      Manage
      <svg viewBox="0 0 20 20" fill="currentColor">
        <path fill-rule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clip-rule="evenodd" />
      </svg>
    </button>
  </div>
  {/if}

  <div class="overview-card">
    <div class="overview-icon integrations-icon">
      <svg viewBox="0 0 20 20" fill="currentColor">
        <path d="M10 3.5a1.5 1.5 0 013 0V4a1 1 0 001 1h3a1 1 0 011 1v3a1 1 0 01-1 1h-.5a1.5 1.5 0 000 3h.5a1 1 0 011 1v3a1 1 0 01-1 1h-3a1 1 0 01-1-1v-.5a1.5 1.5 0 00-3 0v.5a1 1 0 01-1 1H6a1 1 0 01-1-1v-3a1 1 0 00-1-1h-.5a1.5 1.5 0 010-3H4a1 1 0 001-1V6a1 1 0 011-1h3a1 1 0 001-1v-.5z" />
      </svg>
    </div>
    <div class="overview-content">
      <h3>Integrations</h3>
      <p>Connect external services</p>
    </div>
    <button class="overview-action" onclick={() => onNavigate('integrations')}>
      Configure
      <svg viewBox="0 0 20 20" fill="currentColor">
        <path fill-rule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clip-rule="evenodd" />
      </svg>
    </button>
  </div>
</div>

<!-- Help link for welcome email -->
<ResendWelcomeEmail />

<style>
  /* Welcome Banner */
  .welcome-banner {
    background: linear-gradient(135deg, #4A7DFF 0%, #6B5CE7 100%);
    border-radius: 16px;
    padding: 2rem;
    margin-bottom: 1.5rem;
    color: white;
  }

  .welcome-content {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 2rem;
    flex-wrap: wrap;
  }

  .welcome-text h2 {
    margin: 0 0 0.5rem;
    font-size: 1.5rem;
    font-weight: 600;
  }

  .welcome-text p {
    margin: 0;
    opacity: 0.9;
    font-size: 0.95rem;
  }

  .launch-app-btn {
    display: inline-flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0.875rem 1.5rem;
    background: white;
    color: #4A7DFF;
    border-radius: 10px;
    font-weight: 600;
    font-size: 0.95rem;
    text-decoration: none;
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }

  .launch-app-btn:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.2);
  }

  .launch-app-btn svg {
    width: 18px;
    height: 18px;
  }

  /* Launch App Card */
  .launch-app-card {
    display: none;
    background: white;
    border-radius: 16px;
    padding: 1.5rem;
    margin-bottom: 1.5rem;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    align-items: center;
    gap: 1rem;
    border: 2px solid #4A7DFF;
  }

  .launch-app-icon {
    width: 56px;
    height: 56px;
    flex-shrink: 0;
    color: #4A7DFF;
  }

  .launch-app-icon svg {
    width: 100%;
    height: 100%;
  }

  .launch-app-content {
    flex: 1;
  }

  .launch-app-content h3 {
    margin: 0 0 0.25rem;
    font-size: 1.1rem;
    color: #1e1e2e;
  }

  .launch-app-content p {
    margin: 0;
    font-size: 0.85rem;
    color: #666;
  }

  .launch-app-action {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.75rem 1.25rem;
    background: linear-gradient(135deg, #4A7DFF 0%, #6B5CE7 100%);
    color: white;
    border-radius: 10px;
    font-weight: 600;
    font-size: 0.9rem;
    text-decoration: none;
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  }

  .launch-app-action:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(74, 125, 255, 0.4);
  }

  .launch-app-action svg {
    width: 16px;
    height: 16px;
  }

  /* Overview Grid */
  .overview-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    gap: 1.5rem;
  }

  .overview-card {
    background: white;
    border-radius: 12px;
    padding: 1.5rem;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    display: flex;
    align-items: center;
    gap: 1rem;
    transition: transform 0.2s, box-shadow 0.2s;
  }

  .overview-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }

  .overview-icon {
    width: 48px;
    height: 48px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  .overview-icon svg {
    width: 24px;
    height: 24px;
  }

  .subscription-icon {
    background: linear-gradient(135deg, #4A7DFF 0%, #6B5CE7 100%);
    color: white;
  }

  .business-icon {
    background: #dbeafe;
    color: #1d4ed8;
  }

  .team-icon {
    background: #d1fae5;
    color: #065f46;
  }

  .integrations-icon {
    background: #fef3c7;
    color: #d97706;
  }

  .overview-content {
    flex: 1;
  }

  .overview-content h3 {
    margin: 0 0 0.25rem;
    font-size: 1rem;
    color: #333;
  }

  .overview-content p {
    margin: 0;
    font-size: 0.85rem;
    color: #666;
  }

  .overview-content .status-active {
    color: #059669;
    font-weight: 500;
  }

  .overview-content .status-none {
    color: #9ca3af;
  }

  .overview-action {
    display: flex;
    align-items: center;
    gap: 0.25rem;
    padding: 0.5rem 0.75rem;
    background: #f3f4f6;
    border: none;
    border-radius: 6px;
    color: #4A7DFF;
    font-weight: 500;
    font-size: 0.85rem;
    cursor: pointer;
    transition: background 0.2s;
  }

  .overview-action:hover {
    background: #e5e7eb;
  }

  .overview-action svg {
    width: 16px;
    height: 16px;
  }

  /* Responsive */
  @media (max-width: 768px) {
    .welcome-banner .launch-app-btn {
      display: none;
    }

    .welcome-banner {
      padding: 1.5rem;
    }

    .welcome-text h2 {
      font-size: 1.25rem;
    }

    .launch-app-card {
      display: flex;
    }
  }
</style>
